import { Body2, Image, Label } from '@cian/ui-kit';

import { type FC, type JSX, useCallback, useMemo } from 'react';

import { type RoomsInfoItem, RoomsList } from 'shared/components/RoomsList';
import { Transport } from 'shared/components/Transport';
import { WrapElementsSpacer } from 'shared/components/WrapElementsSpacer';
import { type RemotenessTypes } from 'shared/types/RemotenessTypes';
import { type TransportTypes } from 'shared/types/TransportTypes';
import { extractUndefined } from 'shared/utils/extractUndefined';
import { splitStringToMaxCharsInRow } from 'shared/utils/splitStringToMaxCharsInRow';

import styles from './ResidentialComplexesCardSeriaPlus.css';

type TransportType = {
  id: number;
  name: string;
  iconColor?: string | null;
  remoteness?: string | null;
  remotenessType?: RemotenessTypes | null;
  iconType?: TransportTypes | null;
};

type ResidentialComplexesCardSeriaPlusProps = {
  title: string;
  subtitle: string | null;
  transports: TransportType[];
  imageUrl: string;
  roomsInfo: RoomsInfoItem[];
  promoLabel: string | null;
  url: string;
  newbuildingId: number;
  onJkCardClick: (newbuildingId: number) => void;
};

export const ResidentialComplexesCardSeriaPlus: FC<ResidentialComplexesCardSeriaPlusProps> = ({
  title,
  subtitle,
  transports,
  imageUrl,
  roomsInfo,
  promoLabel,
  url,
  newbuildingId,
  onJkCardClick,
}) => {
  const handleClick = useCallback((): void => {
    onJkCardClick(newbuildingId);
  }, [newbuildingId, onJkCardClick]);

  const splitTitleViews = useMemo<Array<string | JSX.Element>>(() => {
    const splitTitle = splitStringToMaxCharsInRow(title, 28);
    const lastSplitTitleIndex = splitTitle.length - 1;

    const result = Array.of<string | JSX.Element>();

    for (let index = 0; index < lastSplitTitleIndex; index += 1) {
      result.push(splitTitle[index], <br key={`br_${index}`} />);
    }

    result.push(
      <WrapElementsSpacer horizontalSpace={8} key={lastSplitTitleIndex + 1} verticalSpace={4}>
        {splitTitle[lastSplitTitleIndex]}
        <Label background="theme-gray" color="text-inverted-default" data-testid="seria-plus-label">
          Серия плюс
        </Label>
      </WrapElementsSpacer>,
    );

    return result;
  }, [title]);

  return (
    <a
      className={styles['container']}
      data-testid="residential-complexes-card-seria-plus"
      href={url}
      rel="noopener noreferrer"
      target="_blank"
      onClick={handleClick}
    >
      <div className={styles['image-wrapper']}>
        {!!promoLabel && (
          <div className={styles['promo-label-wrapper']}>
            <Label background="control-inverted-primary-default" color="#fc4c02" data-testid="promo-label">
              {promoLabel}
            </Label>
          </div>
        )}
        <div className={styles['image-wrapper-inner']}>
          <Image
            alt={title}
            data-testid="image"
            height="100%"
            loading="lazy"
            objectFit="cover"
            src={imageUrl}
            width="100%"
          />
        </div>
      </div>
      <div className={styles['content']}>
        <div className={styles['title']} data-testid="title">
          {splitTitleViews}
        </div>
        {!!subtitle && (
          <Body2 color="text-secondary-default" data-testid="subtitle">
            {subtitle}
          </Body2>
        )}

        {!!transports.length && (
          <div className={styles['transports-wrapper']} data-testid="transports-wrapper">
            {transports.map(transport => (
              <Transport
                iconColor={extractUndefined(transport.iconColor)}
                iconType={extractUndefined(transport.iconType)}
                key={transport.id}
                name={transport.name}
                remoteness={extractUndefined(transport.remoteness)}
                remotenessIconColor="icon-secondary-default"
                remotenessType={extractUndefined(transport.remotenessType)}
                textColor="text-secondary-default"
              />
            ))}
          </div>
        )}

        {!!roomsInfo.length && (
          <div className={styles['rooms-info-wrapper']} data-testid="rooms-list-wrapper">
            <RoomsList list={roomsInfo} maxItems={6} />
          </div>
        )}
      </div>
    </a>
  );
};
